import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Inbox, Mail, Users, CalendarDays, Euro, BarChart3, Search, ChevronDown, UserCircle } from 'lucide-react';
import { useFeatures } from '../../hooks/useFeatures';
import { useAuth, api } from '../../context/AuthContext';
import { sucheOeffnen } from './GlobalSuche';
import { SeitenHinweis, HinweisSchalter } from '../hilfe/SeitenHinweis';

const TEAM = ['admin', 'kanzlei_mitarbeiter', 'freelancer'];
export const KANZLEI_PFAD = /^\/((admin|kanzlei|team-portal)(\/|$)|team\/)/;
export const eingangAktualisieren = () => window.dispatchEvent(new Event('eingang-aktualisiert'));

const NAV = [
  { to: '/kanzlei/eingang', label: 'Dashboard', tipp: 'Wochenplan und Eingang: alles, was heute eine Reaktion braucht', icon: Inbox, zaehler: true, id: 'eingang', match: /^\/kanzlei\/eingang/ },
  { to: '/kanzlei/nachrichten', label: 'Nachrichten', tipp: 'Postfach wie Outlook: Portal-Chats, Website-Anfragen, Rückrufe und E-Mails – lesen, antworten, verknüpfen', icon: Mail, id: 'nachrichten', match: /^\/(kanzlei\/nachrichten|team-portal)/ },
  { to: '/kanzlei/mandanten', label: 'Mandanten', tipp: 'Mandanten suchen, 360°-Ansicht, Akten, Dokumente und Portalzugänge', icon: Users, id: 'mandanten', voll: true, match: /^\/kanzlei\/(mandanten|akten|kontakte|konten|dokumente|mandatsunterlagen|portalansicht|signaturen|cobrowsing)/ },
  { to: '/kanzlei/planer', freelancerTo: '/team/tickets', label: 'Aufgaben & Termine', tipp: 'Aufgaben, Gesprächsnotizen, Termine, Rückrufe und Outlook-Abgleich', icon: CalendarDays, id: 'planer', match: /^\/(kanzlei\/(planer|termine|rueckrufe|kalender|outlook)|team\/(rueckrufe|tickets))/ },
  { to: '/kanzlei/zahlungen', label: 'Zahlungen', tipp: 'Rechnungen, Akonto, Lastschriften, Zahlungsabgleich und Mahnlauf', icon: Euro, id: 'zahlungen', voll: true, match: /^\/kanzlei\/zahlungen/ },
  { to: '/kanzlei/vorlagen', adminTo: '/admin/einstellungen', label: 'Admin', tipp: 'Einstellungen mit Vorlagen, Benutzer, Website, Auswertungen und Schnittstellen', icon: BarChart3, id: 'admin', voll: true, match: /^\/(admin\/(analyse|einstellungen|akten|arbeitszeit|schnittstellen|zeitstrahl)|kanzlei\/(vorlagen|benutzer|website))/ },
  { to: '/kanzlei/arbeitszeit', label: 'Mein Bereich', tipp: 'Ihre Arbeitszeit, Urlaub und Ihr Konto mit Zwei-Faktor-Anmeldung', icon: UserCircle, id: 'mein', match: /^\/kanzlei\/(arbeitszeit|urlaub|profil)/ },
];

// [pfad, label, aktiv-regex, nur Vollzugriff, feature, nur Admin]
const UNTER = [];
const TAB_ZAEHLER = { '/kanzlei/mandanten/offen': 'nicht_zugeordnet', '/kanzlei/outlook': 'outlook', '/kanzlei/zahlungen/rechnungen': 'rechnungen', '/kanzlei/zahlungen/akonto': 'akonto' };
const NAV_ZAEHLER = { nachrichten: ['nachrichten'], mandanten: ['nicht_zugeordnet'], planer: ['outlook'], zahlungen: ['rechnungen', 'akonto'] };
const Zahl = ({ n, testId }) => (n > 0 ? <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#b8893d] text-[#0B1724] text-[10.5px] font-semibold inline-flex items-center justify-center" data-testid={testId}>{n}</span> : null);

export const useMenueZaehler = () => {
  const [z, setZ] = useState({});
  useEffect(() => {
    let alive = true;
    const load = () => { if (!document.hidden) api.get('/kanzlei/menue-zaehler').then((r) => alive && setZ(r.data)).catch(() => {}); };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('termin-aktualisiert', load);
    window.addEventListener('eingang-aktualisiert', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('termin-aktualisiert', load); window.removeEventListener('eingang-aktualisiert', load); };
  }, []);
  return z;
};

export const hatUnterleiste = (pathname) => UNTER.some((u) => u.match.test(pathname));

const Unterleiste = ({ pathname, rolle, zaehler }) => {
  const features = useFeatures();
  const [auf, setAuf] = useState(false);
  const u = UNTER.find((x) => x.match.test(pathname));
  useEffect(() => { setAuf(false); }, [pathname]);
  if (!u) return null;
  const tabs = u.tabs.filter(([, , , voll, feature, admin]) => (!voll || rolle !== 'freelancer') && (!admin || rolle === 'admin') && (!feature || features[feature]));
  const akt = tabs.find(([to, , rx]) => (rx ? rx.test(pathname) : pathname === to)) || tabs[0];
  const summe = tabs.reduce((s, [to]) => s + (zaehler[TAB_ZAEHLER[to]] || 0), 0);
  return (
    <div className="border-t border-white/10" data-testid={`unterleiste-${u.id}`}>
      <nav className="max-w-[1500px] mx-auto px-4 lg:px-8 h-10 flex items-center gap-2 relative" aria-label={u.id}>
        <button type="button" onClick={() => setAuf(!auf)} aria-expanded={auf} data-testid="unter-wahl" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-white/85 hover:text-white hover:bg-white/10 transition-colors">
          {akt?.[1]}<Zahl n={summe} testId="zaehler-unter" /><ChevronDown className={`w-3.5 h-3.5 transition-transform ${auf ? 'rotate-180' : ''}`} /></button>
        {auf && <div className="absolute left-4 lg:left-8 top-9 z-50 min-w-[220px] rounded-lg bg-[#10243A] border border-white/10 shadow-xl py-1" data-testid="unter-menue">
          {tabs.map(([to, l, rx]) => {
            const aktiv = rx ? rx.test(pathname) : pathname === to;
            return <Link key={to} to={to} data-testid={`unter-tab-${to.split('/').filter(Boolean).join('-')}`} aria-current={aktiv ? 'page' : undefined}
              className={`flex items-center justify-between px-3 py-2 text-[12.5px] transition-colors ${aktiv ? 'text-[#e0b56a]' : 'text-white/75 hover:text-white hover:bg-white/5'}`}>{l}<Zahl n={zaehler[TAB_ZAEHLER[to]]} testId={`zaehler-${TAB_ZAEHLER[to]}`} /></Link>;
          })}
        </div>}
      </nav>
    </div>
  );
};

export const useEingangAnzahl = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => { if (!document.hidden) api.get('/kanzlei/eingang/anzahl').then((r) => alive && setN(r.data.anzahl)).catch(() => {}); };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('eingang-aktualisiert', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('eingang-aktualisiert', load); };
  }, []);
  return n;
};

const istAktiv = (n, pathname) => (n.match ? n.match.test(pathname) : false);
const LINK = 'relative shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-[10px] text-[13px] transition-colors duration-200';

const NavPunkt = ({ n, aktiv, anzahl, mess, zaehler = {} }) => {
  const Icon = n.icon;
  const inhalt = (<>
    {aktiv && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-[#e0b56a] shadow-[0_0_12px_#e0b56a]" />}
    <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
    <span className="whitespace-nowrap">{n.label}</span>
    {n.zaehler && anzahl > 0 && <span key={anzahl} className="ml-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#b8893d] text-[#0B1724] text-[11px] font-semibold inline-flex items-center justify-center motion-safe:animate-[pulse_1s_ease-in-out_1]" data-testid={mess ? undefined : 'kanzlei-eingang-zaehler'}>{anzahl}</span>}
    {!mess && <Zahl n={(NAV_ZAEHLER[n.id] || []).reduce((s, k) => s + (zaehler[k] || 0), 0)} testId={`nav-zaehler-${n.id}`} />}
  </>);
  if (mess) return <span data-mess className={LINK}>{inhalt}</span>;
  return <Link to={n.to} data-tipp={n.tipp} data-testid={`kanzlei-nav-${n.id}`} aria-current={aktiv ? 'page' : undefined} className={`${LINK} ${aktiv ? 'text-white bg-white/10' : 'text-white/65 hover:text-white hover:bg-white/5'}`}>{inhalt}</Link>;
};

const Leiste = ({ rolle }) => {
  const { pathname } = useLocation();
  const anzahl = useEingangAnzahl();
  const items = NAV.filter((n) => !n.voll || rolle !== 'freelancer').map((n) => (rolle === 'freelancer' && n.freelancerTo ? { ...n, to: n.freelancerTo } : rolle === 'admin' && n.adminTo ? { ...n, to: n.adminTo } : n));
  const navRef = useRef(null);
  const zaehler = useMenueZaehler();
  useLayoutEffect(() => {
    const nav = navRef.current;
    const a = nav?.querySelector('[aria-current="page"]');
    if (nav && a && nav.scrollWidth > nav.clientWidth) nav.scrollLeft = a.offsetLeft - (nav.clientWidth - a.offsetWidth) / 2;
  }, [pathname, zaehler]);
  const laufend = zaehler.laufender_termin;
  return (
    <div className="fixed left-0 right-0 top-[104px] lg:top-[152px] z-40 bg-[#0B1724]/95 backdrop-blur-[16px] border-b border-[#b8893d]/40 shadow-[0_8px_24px_-16px_rgba(11,23,36,.6)]" data-testid="kanzlei-leiste">
      <div className="max-w-[1500px] mx-auto px-4 lg:px-8 flex items-center gap-2 h-14">
        <nav ref={navRef} className="relative flex items-center gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0 max-sm:[mask-image:linear-gradient(to_right,#000_80%,transparent)]" aria-label="Kanzlei-Bereich" data-testid="kanzlei-nav">
          {items.map((n) => <NavPunkt key={n.id} n={n} aktiv={istAktiv(n, pathname)} anzahl={anzahl} zaehler={zaehler} />)}
        </nav>
        {laufend && <Link to={`/kanzlei/termin/${laufend}`} className="shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-[10px] bg-[#e0b56a]/15 border border-[#e0b56a]/50 text-[#e0b56a] text-[13px] font-semibold hover:bg-[#e0b56a]/25" data-testid="kanzlei-laufender-termin">
          <span className="relative flex w-2.5 h-2.5"><span className="absolute inline-flex h-full w-full rounded-full bg-[#e0b56a] opacity-75 animate-ping" /><span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#e0b56a]" /></span><span className="hidden sm:inline">Laufender Termin</span></Link>}
        <HinweisSchalter pathname={pathname} />
        <button type="button" onClick={sucheOeffnen} aria-label="Suchen (Taste /)" data-tipp="Suchen nach Mandant, Akte, Rechnung … (Taste /)" data-testid="kanzlei-suche-oeffnen"
          className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-[10px] text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-200"><Search className="w-[18px] h-[18px]" /></button>
      </div>
      <Unterleiste pathname={pathname} rolle={rolle} zaehler={zaehler} />
      <SeitenHinweis pathname={pathname} />
    </div>
  );
};

export const useKanzleiLeisteSichtbar = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return TEAM.includes(user?.role) && KANZLEI_PFAD.test(pathname);
};

export const KanzleiLeiste = () => {
  const { user } = useAuth();
  const sichtbar = useKanzleiLeisteSichtbar();
  return sichtbar ? <Leiste rolle={user.role} /> : null;
};

export default KanzleiLeiste;
